<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import {
  IActivateProgramCommand,
  useActivateSiteProgramMutation,
} from "@/composables/kili/internal/mutations/use-activate-site-program-mutation";
import {
  IDeleteProgramCommand,
  useDeleteSiteProgramMutation,
} from "@/composables/kili/internal/mutations/use-delete-site-program-mutation";
import {
  TDuplicateProgramCommand,
  useDuplicateProgramMutation,
} from "@/composables/kili/internal/mutations/use-duplicate-program-mutation";
import {
  TRenameProgramCommand,
  useUpdateSiteProgramMutation,
} from "@/composables/kili/internal/mutations/use-update-site-program-mutation";
import { useFetchSiteInstalledApplianceCategories } from "@/composables/kili/sites/fetches/use-fetch-site-installed-appliance-categories";
import { useGetSiteProgramsQuery } from "@/composables/kili/sites/queries/use-get-site-programs-query";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { useToaster } from "@/composables/layout/use-toaster";
import { useBrandedNumberRouteParams } from "@/composables/use-route-params/use-hospitality-number-route-param";
import { useSearchFilter } from "@/composables/use-search-filter";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES } from "@/data/api/kili/programs/program.type";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Skeleton from "primevue/skeleton";

import BaseLayout from "@/components/layout/base-layout.vue";
import ProgramOverviewCard from "@/components/programming/program-overview-card.vue";
import ProgrammingDuplicatingDialog from "@/components/programming/programming-duplicating-dialog.vue";
import ProgrammingRenamingDialog from "@/components/programming/programming-renaming-dialog.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const { brandedNumberRouteParam: siteId } = useBrandedNumberRouteParams<V4HSiteId>("siteId");
const { t, k } = useTypedIntl();
const toast = useToaster();

const { data: programs, isLoading } = useGetSiteProgramsQuery(siteId);
const { data: sites } = useGetSitesQuery();

const { mutateAsync: activateProgram, isPending: isActivatingProgram } = useActivateSiteProgramMutation();
const { mutateAsync: deleteProgram, isPending: isDeletingProgram } = useDeleteSiteProgramMutation();
const { mutateAsync: updateProgram, isPending: isRenamingProgram } = useUpdateSiteProgramMutation();
const { mutateAsync: duplicateProgram, isPending: isDuplicatingProgram } = useDuplicateProgramMutation(
  DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES,
);
const { fetch: fetchInstalledApplianceCategories } = useFetchSiteInstalledApplianceCategories();

const isProgrammingRenamingDialogVisible = ref<boolean>(false);
const isProgrammingDuplicatingDialogVisible = ref<boolean>(false);

const selectedProgram = ref<SiteProgramOverview>();
const activatingProgramId = ref<ProgramId>();

const isNonActivationProgramMutationPending = computed(
  () => isDeletingProgram.value || isRenamingProgram.value || isDuplicatingProgram.value,
);

const isAnyProgramMutationPending = computed(
  () => isActivatingProgram.value || isNonActivationProgramMutationPending.value,
);

const { searchInput: searchProgramInput, filteredList: filteredPrograms } = useSearchFilter<SiteProgramOverview>(
  programs,
  "name",
);

watch(siteId, () => {
  isProgrammingRenamingDialogVisible.value = false;
  isProgrammingDuplicatingDialogVisible.value = false;
  selectedProgram.value = undefined;
});

async function handleActivateProgram(command: IActivateProgramCommand) {
  if (isAnyProgramMutationPending.value) return;

  activatingProgramId.value = command.programId;

  try {
    await activateProgram(command);
  } catch (error) {
    toastKiliError(error, "control.programming.activation.error");
    return;
  } finally {
    activatingProgramId.value = undefined;
  }

  if (command.isActive) {
    MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_ENABLED, {
      name: command.name,
    });
  } else {
    MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_DISABLED, {
      name: command.name,
    });
  }
}

function isProgramCardDisabled(programId: ProgramId) {
  return isNonActivationProgramMutationPending.value || activatingProgramId.value === programId;
}

async function handleSelectProgram(programId: ProgramId) {
  if (isAnyProgramMutationPending.value) return;

  await router.push({
    name: ERouteName.PROGRAMMING_DETAILS,
    params: { siteId: siteId.value, programId },
  });
}

async function handleOpenProgramCreation() {
  if (isAnyProgramMutationPending.value) return;

  MixpanelService.trackEvent(EMixpanelEvent.CREATE_PROGRAM);

  await router.push({
    name: ERouteName.PROGRAMMING_CREATION,
    params: { siteId: siteId.value },
  });
}

async function handleDeleteProgram(command: IDeleteProgramCommand) {
  if (isAnyProgramMutationPending.value) return;

  try {
    await deleteProgram(command);

    toast.add({
      severity: "success",
      detail: t(k.control.programming.deletion.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_DELETED);
  } catch (e) {
    toastKiliError(e, "control.programming.deletion.error");
  }
}

function handleOpenRenameProgramDialog(program: SiteProgramOverview) {
  if (isAnyProgramMutationPending.value) return;

  selectedProgram.value = program;
  isProgrammingRenamingDialogVisible.value = true;
}

async function handleRenameProgram(newName: string) {
  if (isAnyProgramMutationPending.value) return;

  if (newName === selectedProgram.value?.name) {
    isProgrammingRenamingDialogVisible.value = false;
    return;
  }

  try {
    if (!selectedProgram.value) {
      throw new Error("There were no selected program for renaming");
    }

    await updateProgram({
      siteId: selectedProgram.value.siteId,
      programId: selectedProgram.value.id,
      request: {
        name: newName,
      },
    } satisfies TRenameProgramCommand);

    isProgrammingRenamingDialogVisible.value = false;

    MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_RENAMED, {
      name: newName,
    });
  } catch (e) {
    toastKiliError(e, "control.programming.renaming.error");
  }
}

function handleOpenDuplicateProgramDialog(program: SiteProgramOverview) {
  if (isAnyProgramMutationPending.value) return;

  selectedProgram.value = program;
  isProgrammingDuplicatingDialogVisible.value = true;
}

async function handleDuplicateProgram(command: TDuplicateProgramCommand) {
  if (isAnyProgramMutationPending.value) return;

  try {
    if (!selectedProgram.value) {
      throw new Error("There were no selected program for duplicating");
    }

    await duplicateProgram(command);

    isProgrammingDuplicatingDialogVisible.value = false;

    toast.add({
      severity: "success",
      detail: t(k.control.programming.copyToOtherSite.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_COPIED, {
      name: command.copiedSiteProgram.name,
    });
  } catch (e) {
    toastKiliError(e, "control.programming.copyToOtherSite.error");
  }
}

onMounted(async () => {
  // prefetch installed appliance categories on the site
  await fetchInstalledApplianceCategories(siteId.value);
});
</script>

<template>
  <BaseLayout
    :subtitle="t(k.control.programming.subtitle)"
    :title="t(k.control.programming.title)"
  >
    <template #titleRight>
      <HospitalityButton
        data-test-id="create-program-button"
        :disabled="isAnyProgramMutationPending"
        @click="handleOpenProgramCreation"
      >
        <i
          aria-hidden="true"
          class="pi pi-plus text-[18px] pr-2"
        />
        {{ t(k.control.programming.createProgram) }}
      </HospitalityButton>
    </template>

    <div class="flex flex-col gap-4 pb-6">
      <div class="flex gap-4">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText
            v-model.trim="searchProgramInput"
            :placeholder="t(k.control.programming.searchProgram)"
          />
        </IconField>
      </div>

      <div class="flex flex-col">
        <h3 v-if="filteredPrograms && !isLoading">
          {{ t(k.control.programming.countPrograms({ count: filteredPrograms.length })) }}
        </h3>

        <Skeleton
          v-else
          class="!w-40 !h-8"
        />
        <span class="text-text-secondary">{{ t(k.control.programming.subtitle) }}</span>
      </div>
    </div>

    <div class="flex flex-grow overflow-auto flex-col gap-6 pb-8 w-full">
      <div class="md:grid max-md:flex max-md:flex-col xl:grid-cols-3 max-xl:grid-cols-2 gap-4">
        <ProgramOverviewCard
          v-for="program in filteredPrograms"
          :key="program.id"
          :disabled="isProgramCardDisabled(program.id)"
          :is-active="program.isActive"
          :program="program"
          :site-id="siteId"
          @delete="handleDeleteProgram($event)"
          @duplicate="handleOpenDuplicateProgramDialog($event)"
          @rename="handleOpenRenameProgramDialog($event)"
          @select="handleSelectProgram($event)"
          @toggle="handleActivateProgram($event)"
        />
      </div>
    </div>
  </BaseLayout>

  <ProgrammingRenamingDialog
    v-model="isProgrammingRenamingDialogVisible"
    :is-pending="isRenamingProgram"
    :program="selectedProgram"
    @submit="handleRenameProgram($event)"
  />

  <ProgrammingDuplicatingDialog
    v-model="isProgrammingDuplicatingDialogVisible"
    :duplicated-program="selectedProgram"
    :is-pending="isDuplicatingProgram"
    :sites="sites"
    @submit="handleDuplicateProgram($event)"
  />
</template>
